import React, { useState, useEffect, useMemo } from 'react';
import PrimaryModal from '../../../../components/UI/Modal/PrimaryModal';
import { ErrorButton, PrimaryButton } from "../../../../components/UI";
import { useUserContext } from '../../../../contexts/UserContext';
import { useContentfulContext } from '../../../../contexts/ContentfulContext';
import { toast } from 'react-toastify';
import AgGridWrapper from '../../../../components/UI/AgGrid';
import ConfirmModal from '../../../../components/UI/Modal/confirmModal';
import { deleteSpecificGithubIntegration } from '../../../../utils/APIs';

const DeleteIntegrationModal = ({ setDeleteIntegration, id, setActionLoading, getGithubData }) => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();

    const [dependencies, setDependencies] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [showGrid, setShowGrid] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const modalText = content.GITHUB_CONTENT.delete;

    useEffect(() => {
        const fetchDependencies = async () => {
            try {
                setLoading(true);
                const result = await deleteSpecificGithubIntegration(
                    userData.token,
                    userData.selectedOrganization.org_token,
                    id,
                    true
                );

                if (result?.data && Object.keys(result.data).length > 0) {
                    setDependencies(result.data.data);
                    setMessage(result.data.message);
                }
            } catch (err) {
                setError(err?.message || modalText.error.fetch_failed);
            } finally {
                setLoading(false);
            }
        };
        fetchDependencies();
    }, [id, userData, modalText]);

    const handleDeleteProduct = async () => {
        try {
            setActionLoading(true);
            const result = await deleteSpecificGithubIntegration(
                userData.token,
                userData.selectedOrganization.org_token,
                id
            );

            if (result) {
                const updateDefault = userData?.defaultAdobeIO?._id === id;
                await getGithubData(updateDefault);
                setDeleteIntegration(false);
                toast.success(result?.message);
            }
        } catch (err) {
            setError(err?.message || modalText.error.delete_failed);
        } finally {
            setActionLoading(false);
            setShowConfirm(false);
        }
    };

    const rowData = useMemo(() => {
        const rows = [];
        if (dependencies?.experience_offer_mappings) {
            rows.push(
                ...dependencies.experience_offer_mappings.map(d => ({
                    type: 'Experience Mapping',
                    name: d.experience_name,
                    id: d.records.map(r => r.integration_id).join(", "),
                    ...d
                }))
            );
        }
        return rows;
    }, [dependencies]);

    const colDefs = [
        { headerName: 'Type', field: 'type', flex: 1 },
        { headerName: 'Name', field: 'name', flex: 2 },
        { headerName: 'ID', field: 'id', flex: 2 }
    ];

    return (
        <>
            <PrimaryModal title={modalText.title} hideModal={setDeleteIntegration}>
                <div className="p-6 space-y-6">
                    {loading ? (
                        <p className="text-center text-gray-500">
                            {modalText.checking_dependencies}
                        </p>
                    ) : (
                        <>
                            {!showGrid && (
                                <>
                                    {message ? (
                                        <p className="text-base text-black mb-4">
                                            {message} <br />
                                            <span className="font-semibold text-red-500">
                                                {modalText.warning}
                                            </span>
                                        </p>
                                    ) : (
                                        <p className="text-base text-black mb-4">
                                            {modalText.description}
                                        </p>
                                    )}

                                    {error && <p className="text-red-500">{error}</p>}
                                    <div className="mb-4 flex justify-end space-x-4">
                                        {message ? (
                                            <>
                                                <PrimaryButton handleClick={() => setShowGrid(true)}>
                                                    {modalText.review_dependencies_cta}
                                                </PrimaryButton>
                                                <ErrorButton handleClick={() => setShowConfirm(true)}>
                                                    {modalText.delete_integration_cta}
                                                </ErrorButton>
                                            </>
                                        ) : (
                                            <ErrorButton handleClick={() => handleDeleteProduct()}>
                                                {modalText.formLabels.deactivate_integration_cta}
                                            </ErrorButton>
                                        )}
                                    </div>
                                </>
                            )}

                            {showGrid && (
                                <div>
                                    <p className="mb-2 font-semibold">
                                        {modalText.dependencies_preview}
                                    </p>
                                    <div style={{ height: "300px", overflow: "auto", width: "100%" }}>
                                        <AgGridWrapper
                                            tableId="github-dependencies-table"
                                            rowData={rowData}
                                            columnDefs={colDefs}
                                        />
                                    </div>
                                    <div className="mt-4 flex justify-end space-x-4">
                                        <PrimaryButton handleClick={() => setShowGrid(false)}>
                                            {modalText.back}
                                        </PrimaryButton>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </PrimaryModal>

            {showConfirm && (
                <ConfirmModal
                    title={modalText.confirm.title}
                    description={modalText.confirm.description}
                    ConfirmText={modalText.confirm.confirm_text}
                    error={error}
                    loading={false}
                    hideModal={setShowConfirm}
                    handleConfirmation={(confirmed) => {
                        if (confirmed) handleDeleteProduct();
                        else setShowConfirm(false);
                    }}
                />
            )}
        </>
    );
};

export default DeleteIntegrationModal;